import {
  Checkbox,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import PhysicalCardTooltip from 'src/components/PhysicalCardTooltip/PhysicalCardTooltip'
import RadioItems from 'src/components/RadioItems/RadioItems'
import TextDropdown from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import { CountryFieldModel } from 'src/models/country.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { FormProps } from 'src/types'
import { css } from 'styled-components'

export type PhysicalVirtualCardAddressFlatFields = {
  country?: string
  address1?: string
  address2?: string
  city?: string
  province?: string
  postal?: string
}

interface PhysicalVirtualCardBoxProps {
  className?: string
  includePhysicalCard?: boolean
  onCheckboxSelect: (val: boolean) => void
  plasticFirstNameProps?: FormProps<string>
  plasticLastNameProps?: FormProps<string>
  currentBillingAddress: React.ReactElement
  customShippingAddress?: boolean
  onButtonSelect: (val: boolean) => void
  countrySchema: CountryFieldModel[]
  flatFields: PhysicalVirtualCardAddressFlatFields
  flatFieldsErrors: Partial<{
    [field in keyof PhysicalVirtualCardAddressFlatFields]: Array<
      React.ReactElement | string
    >
  }>
  onFlatFieldChange: (
    fieldName: keyof PhysicalVirtualCardAddressFlatFields,
    value: PhysicalVirtualCardAddressFlatFields[keyof PhysicalVirtualCardAddressFlatFields],
  ) => void
}

const PhysicalVirtualCardBox: React.FC<PhysicalVirtualCardBoxProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const translate = useTranslate()

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          z-index: 1;
        `}
      >
        <Checkbox
          isSelected={!!props.includePhysicalCard}
          label={<Trns source={'Include a physical card'} />}
          boldLabel
          onClick={(): void => {
            props.onCheckboxSelect(!props.includePhysicalCard)
          }}
        />
        <PhysicalCardTooltip openLeft />
      </div>
      {props.includePhysicalCard && (
        <form
          css={css`
            margin-left: 30px;
          `}
        >
          {props.plasticFirstNameProps !== undefined && (
            <TextInputField
              css={css`
                margin-top: 30px;
              `}
              fieldLabel={<Trns source={'First Name on Physical Card'} />}
              design={TextInputFieldDesigns.New}
              errors={props.plasticFirstNameProps.errors}
              placeholder={translateSingleString(locale, 'Enter first name')}
              value={props.plasticFirstNameProps.value}
              onChange={props.plasticFirstNameProps.onChange}
            />
          )}
          {props.plasticLastNameProps !== undefined && (
            <TextInputField
              css={css`
                margin-top: 30px;
              `}
              fieldLabel={<Trns source={'Last Name on Physical Card'} />}
              design={TextInputFieldDesigns.New}
              errors={props.plasticLastNameProps.errors}
              placeholder={translateSingleString(locale, 'Enter last name')}
              value={props.plasticLastNameProps.value}
              onChange={props.plasticLastNameProps.onChange}
            />
          )}
          <RadioItems
            css={css`
              margin-top: 30px;
            `}
            alignStart
            noBorder
            staticOnHover
            fieldLabel={<Trns source={'Shipping Address'} />}
            items={[
              <div
                key={0}
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  display: flex;
                  flex-direction: column;

                  ${!props.customShippingAddress &&
                  css`
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                `}
              >
                <span>
                  <Trns source={'Billing Address'} />
                </span>
                <span>{props.currentBillingAddress}</span>
              </div>,
              <div
                key={1}
                css={css`
                  display: flex;
                  flex-direction: column;
                  flex-grow: 2;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                    ${!!props.customShippingAddress &&
                    css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  `}
                >
                  <Trns source={'Custom Shipping Address'} />
                </span>
                {props.customShippingAddress &&
                  props.countrySchema.map((field, i) => (
                    <Fragment key={i}>
                      {field.options !== undefined && (
                        <TextDropdown
                          css={css`
                            margin-top: 30px;
                          `}
                          errors={props.flatFieldsErrors[field.name]}
                          placeholder={translateSingleString(
                            locale,
                            'Select ${fieldLabel}',
                            { fieldLabel: field.label },
                          )}
                          fieldLabel={field.label}
                          isMulti={false}
                          selectedValues={
                            props.flatFields[field.name] !== undefined
                              ? new Set([
                                  props.flatFields[field.name] as string,
                                ])
                              : new Set([])
                          }
                          chooseOption={(val: string): void => {
                            props.onFlatFieldChange(field.name, val)
                          }}
                          optionGroups={[
                            {
                              options: field.options.map((opt) => ({
                                value: opt.code,
                                details: {
                                  text: opt.label,
                                },
                              })),
                            },
                          ]}
                        />
                      )}
                      {field.options === undefined && (
                        <TextInputField
                          css={css`
                            margin-top: 30px;
                          `}
                          errors={props.flatFieldsErrors[field.name]}
                          design={TextInputFieldDesigns.New}
                          fieldLabel={field.label}
                          value={props.flatFields[field.name]}
                          onChange={(val: string): void => {
                            props.onFlatFieldChange(field.name, val)
                          }}
                          placeholder={translateSingleString(
                            locale,
                            'Enter ${fieldLabel}',
                            { fieldLabel: field.label },
                          )}
                        />
                      )}
                    </Fragment>
                  ))}
              </div>,
            ]}
            currentIndex={props.customShippingAddress ? 1 : 0}
            setIndex={(val: number): void => {
              props.onButtonSelect(val === 1)
            }}
            ariaLabels={[
              translate('Billing Address'),
              translate('Custom Shipping Address'),
            ]}
          />
        </form>
      )}
    </Box>
  )
}

export default PhysicalVirtualCardBox
